/**
 * Sync rejection toast (Phase 8).
 *
 * Maps `op-rejected.reason` codes to user-facing copy and dedups
 * repeat rejections per (deckId, reason) within a 30-second window
 * so a chatty server doesn't bury the user under identical toasts.
 *
 * The actual revert-both pre-image restore lands in Phase 8b alongside
 * Phase 5 slice 2 (pre-image stash). This module owns ONLY the toast
 * surface today; the revert wiring will hook in here when it lands.
 */

import { emitSharedToast } from '../toast-sink';

export type RejectionReason =
  | 'unauthorized'
  | 'wrong-scope-shared-on-linked'
  | 'wrong-scope-personal-on-shared'
  | 'stale-lww'
  | 'rpc-failed'
  | 'timeout'
  | 'validation'
  | 'unknown';

/** DS-Toast shape: title = the outcome, description = the reason ("so
 *  what?"). English only — this fires from shared non-React code with no
 *  i18n runtime; the copy mirrors the approved `app-sync-op-rejected`
 *  catalog entry. */
const REASON_COPY: Record<RejectionReason, { title: string; description: string }> = {
  unauthorized: {
    title: 'Change not allowed',
    description: "You don't have permission to edit this deck.",
  },
  'wrong-scope-shared-on-linked': {
    title: "Couldn't save your change",
    description: 'Please reload and try again.',
  },
  'wrong-scope-personal-on-shared': {
    title: "Couldn't save your change",
    description: 'Please reload and try again.',
  },
  'stale-lww': {
    title: 'Change not applied',
    description: 'Someone else made a newer edit.',
  },
  'rpc-failed': {
    title: "Couldn't save your change",
    description: 'Connection issue. Please try again.',
  },
  timeout: {
    title: "Couldn't save your change",
    description: "The server didn't respond, so it was reverted.",
  },
  validation: {
    title: "Couldn't save your change",
    description: "That edit isn't valid.",
  },
  unknown: {
    title: "Couldn't save your change",
    description: 'Something went wrong. Please try again.',
  },
};

const DEDUP_WINDOW_MS = 30_000;

type DedupKey = string; // `${deckIdOrAnon}::${reason}`

/**
 * Per-(deck, reason) toast emitter with a 30s dedup window.
 *
 * Constructed once per session via `getRejectionToastEmitter()` so
 * the dedup map is shared across all op-rejected receivers.
 */
export type RejectionToastEmitter = {
  /** Emit a toast for an op-rejected. No-op if a toast for the same
   *  (deckId, reason) was emitted within the last 30 seconds. */
  emit(args: { deckId?: string; reason: RejectionReason }): void;
  /** Test-only: clear the dedup cache. */
  _resetForTests(): void;
};

export function createRejectionToastEmitter(opts?: {
  /** Inject `Date.now` for deterministic tests. */
  now?: () => number;
  /** Inject the toast sink for tests (defaults to the shared DS sink). */
  emitToast?: (title: string, opts?: { description?: string }) => void;
}): RejectionToastEmitter {
  const now = opts?.now ?? Date.now;
  const emitToast =
    opts?.emitToast ??
    ((title: string, toastOpts?: { description?: string }) =>
      emitSharedToast('default', { title, description: toastOpts?.description }));
  const lastEmitted = new Map<DedupKey, number>();

  return {
    emit({ deckId, reason }): void {
      const key = `${deckId ?? '(unknown-deck)'}::${reason}`;
      const last = lastEmitted.get(key);
      const t = now();
      if (last !== undefined && t - last < DEDUP_WINDOW_MS) return;
      lastEmitted.set(key, t);
      const copy = REASON_COPY[reason] ?? REASON_COPY.unknown;
      emitToast(copy.title, { description: copy.description });
    },
    _resetForTests(): void {
      lastEmitted.clear();
    },
  };
}

let singleton: RejectionToastEmitter | null = null;

export function getRejectionToastEmitter(): RejectionToastEmitter {
  if (!singleton) singleton = createRejectionToastEmitter();
  return singleton;
}

/** Test-only: replace the singleton (for inspection). */
export function _setRejectionToastEmitter(next: RejectionToastEmitter | null): void {
  singleton = next;
}

/** Exposed for tests + Phase 8b wiring. */
export const _reasonCopy = REASON_COPY;
